import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ChatbotProfile } from '../state/chatbot-profile';
import {TestVariation} from '../../test/state/test-variation';

interface SimpleProfile {
  id: string;
  name: string;
  acronym: string;
  cached: number;
}

@Injectable({
  providedIn: 'root'
})
export class ChatbotProfileProviderService {
  private profilesSubject = new BehaviorSubject<ChatbotProfile[]>([]);
  public profiles$ = this.profilesSubject.asObservable();

  private markdownReRenderSubject = new Subject<void>();
  public markdownReRender$ = this.markdownReRenderSubject.asObservable();

  private profileCache = new Map<number, ChatbotProfile[]>();
  private currentChatId: number | null = null;
  private lastUpdateTimestamp = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000;

  private globalProfiles = new Map<string, SimpleProfile>();
  private readonly GLOBAL_CACHE_KEY = 'chatbot-global-profiles';
  private readonly GLOBAL_CACHE_DURATION = 24 * 60 * 60 * 1000;

  // Per-chat set of profile keycloakIds the user has explicitly removed. Persisted so the
  // removal survives reloads and chat switches even though the backend keeps returning the
  // chat's accumulated profiles.
  private hiddenProfiles = new Map<number, Set<string>>();
  private readonly HIDDEN_CACHE_KEY = 'chatbot-hidden-profiles';

  constructor() {
    this.loadGlobalCacheFromStorage();
    this.loadHiddenProfilesFromStorage();
  }

  getHiddenProfileIds(chatId: number): Set<string> {
    return this.hiddenProfiles.get(chatId) ?? new Set<string>();
  }

  hideProfilesForChat(chatId: number, keycloakIds: string[]): void {
    if (!keycloakIds || keycloakIds.length === 0) {
      return;
    }
    const hidden = this.hiddenProfiles.get(chatId) ?? new Set<string>();
    keycloakIds.forEach(id => hidden.add(id));
    this.hiddenProfiles.set(chatId, hidden);
    this.saveHiddenProfilesToStorage();
  }

  unhideProfileForChat(chatId: number, keycloakId: string): void {
    const hidden = this.hiddenProfiles.get(chatId);
    if (hidden && hidden.delete(keycloakId)) {
      if (hidden.size === 0) {
        this.hiddenProfiles.delete(chatId);
      }
      this.saveHiddenProfilesToStorage();
    }
  }

  updateProfiles(profiles: ChatbotProfile[], chatId?: number): void {
    const previousCount = this.profilesSubject.value?.length || 0;

    if (chatId !== undefined && profiles && profiles.length > 0) {
      this.profileCache.set(chatId, profiles);
      this.currentChatId = chatId;
      this.lastUpdateTimestamp = Date.now();
    }

    if (profiles && profiles.length > 0) {
      this.populateGlobalCacheFromProfiles(profiles);
    }

    this.profilesSubject.next(profiles || []);
    const newCount = profiles?.length || 0;

    if (previousCount === 0 && newCount > 0) {
      this.triggerMarkdownReRender();
    }
    else if (newCount > 0 && profiles) {
      this.triggerMarkdownReRender();
    }
  }

  getCurrentProfiles(): ChatbotProfile[] {
    const currentProfiles = this.profilesSubject.value;

    if (currentProfiles && currentProfiles.length > 0) {
      return currentProfiles;
    }

    if (this.currentChatId !== null && this.profileCache.has(this.currentChatId)) {
      const cachedProfiles = this.profileCache.get(this.currentChatId);
      const cacheAge = Date.now() - this.lastUpdateTimestamp;

      if (cachedProfiles && cacheAge < this.CACHE_DURATION) {
        this.profilesSubject.next(cachedProfiles);
        return cachedProfiles;
      }
    }

    return currentProfiles || [];
  }

  setCurrentChat(chatId: number | null): void {
    this.currentChatId = chatId;

    if (chatId === null) {
      // Clear profiles when no chat is selected
      this.profilesSubject.next([]);
      return;
    }

    if (this.profileCache.has(chatId)) {
      const cachedProfiles = this.profileCache.get(chatId);
      if (cachedProfiles) {
        this.profilesSubject.next(cachedProfiles);
      }
    }
  }

  clearCache(chatId?: number): void {
    if (chatId !== undefined) {
      this.profileCache.delete(chatId);
    }
    else {
      this.profileCache.clear();
      this.currentChatId = null;
    }
  }

  replaceNamesWithKeycloakIds(text: string): string {
    const currentProfiles = this.getCurrentProfiles();
    if (!currentProfiles || currentProfiles.length === 0) {
      return text;
    }

    let processedText = text;

    currentProfiles.forEach(profile => {
      if (profile.keycloakId && profile.firstName && profile.lastName) {
        const escapedFirstName = profile.firstName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const escapedLastName = profile.lastName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const fullNamePatterns = [
          `@${escapedFirstName}\\s+${escapedLastName}`,
          `\\b${escapedFirstName}\\s+${escapedLastName}\\b`
        ];

        fullNamePatterns.forEach(pattern => {
          const regex = new RegExp(pattern, 'gi');
          processedText = processedText.replace(regex, profile.keycloakId);
        });
      }
    });

    currentProfiles.forEach(profile => {
      if (profile.keycloakId && profile.lastName) {
        const escapedLastName = profile.lastName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const lastNamePatterns = [
          `@${escapedLastName}\\b`,
          `\\b${escapedLastName}\\b`
        ];

        lastNamePatterns.forEach(pattern => {
          const regex = new RegExp(pattern, 'gi');
          processedText = processedText.replace(regex, profile.keycloakId);
        });
      }
    });

    currentProfiles.forEach(profile => {
      if (profile.keycloakId && profile.firstName) {
        const escapedFirstName = profile.firstName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const conflictingProfiles = currentProfiles.filter(p =>
          p.firstName === profile.firstName &&
          p.keycloakId !== profile.keycloakId
        );

        if (conflictingProfiles.length === 0) {
          const firstNamePatterns = [
            `@${escapedFirstName}\\b`,
            `\\b${escapedFirstName}\\b`
          ];

          firstNamePatterns.forEach(pattern => {
            const regex = new RegExp(pattern, 'gi');
            processedText = processedText.replace(regex, profile.keycloakId);
          });
        }
      }
    });

    return processedText;
  }

  replaceNamesWithAcronyms(text: string): string {
    const currentProfiles = this.getCurrentProfiles();
    if (!currentProfiles || currentProfiles.length === 0) {
      return text;
    }

    let processedText = text;

    // Replace full names with acronyms
    currentProfiles.forEach(profile => {
      if (profile.acronym && profile.firstName && profile.lastName) {
        const escapedFirstName = profile.firstName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const escapedLastName = profile.lastName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const fullNamePatterns = [
          `@${escapedFirstName}\\s+${escapedLastName}`,
          `\\b${escapedFirstName}\\s+${escapedLastName}\\b`
        ];

        fullNamePatterns.forEach(pattern => {
          const regex = new RegExp(pattern, 'gi');
          processedText = processedText.replace(regex, profile.acronym);
        });
      }
    });

    // Replace last names with acronyms
    currentProfiles.forEach(profile => {
      if (profile.acronym && profile.lastName) {
        const escapedLastName = profile.lastName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const lastNamePatterns = [
          `@${escapedLastName}\\b`,
          `\\b${escapedLastName}\\b`
        ];

        lastNamePatterns.forEach(pattern => {
          const regex = new RegExp(pattern, 'gi');
          processedText = processedText.replace(regex, profile.acronym);
        });
      }
    });

    // Replace first names with acronyms (only if no conflicts)
    currentProfiles.forEach(profile => {
      if (profile.acronym && profile.firstName) {
        const escapedFirstName = profile.firstName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const conflictingProfiles = currentProfiles.filter(p =>
          p.firstName === profile.firstName &&
          p.keycloakId !== profile.keycloakId
        );

        if (conflictingProfiles.length === 0) {
          const firstNamePatterns = [
            `@${escapedFirstName}\\b`,
            `\\b${escapedFirstName}\\b`
          ];

          firstNamePatterns.forEach(pattern => {
            const regex = new RegExp(pattern, 'gi');
            processedText = processedText.replace(regex, profile.acronym);
          });
        }
      }
    });

    return processedText;
  }

  replaceKeycloakIdsWithNames(text: string): string {
    let processedText = text;

    const keycloakIdPattern = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

    processedText = processedText.replace(keycloakIdPattern, (keycloakId) => {
      const cachedName = this.getNameFromGlobalCache(keycloakId);
      if (cachedName) {
        return cachedName;
      }

      const currentProfiles = this.getCurrentProfiles();
      if (currentProfiles && currentProfiles.length > 0) {
        const profile = currentProfiles.find(p => p.keycloakId === keycloakId);
        if (profile) {
          let displayName: string;
          if (profile.firstName && profile.lastName &&
              profile.firstName !== 'Unknown' && profile.lastName !== 'User') {
            displayName = `${profile.firstName} ${profile.lastName}`;
          }
          else if (profile.firstName && profile.firstName !== 'Unknown') {
            displayName = profile.firstName;
          }
          else if (profile.lastName && profile.lastName !== 'User') {
            displayName = profile.lastName;
          }
          else {
            displayName = `User-${keycloakId.split('-')[0]}`;
          }

          this.addToGlobalCache(keycloakId, profile.firstName, profile.lastName);
          return displayName;
        }
      }

      return `User-${keycloakId.split('-')[0]}`;
    });

    return processedText;
  }

  replaceAcronymsWithNames(text: string): string {
    let processedText = text;

    // Create a regex pattern to match 6-character acronyms (alphanumeric)
    const acronymPattern = /\b[a-zA-Z0-9]{6}\b/g;

    processedText = processedText.replace(acronymPattern, (acronym) => {
      // First check current profiles
      const currentProfiles = this.getCurrentProfiles();
      if (currentProfiles && currentProfiles.length > 0) {
        const profile = currentProfiles.find(p => p.acronym === acronym);
        if (profile) {
          let displayName: string;
          if (profile.firstName !== 'Unknown' && profile.lastName !== 'User') {
            displayName = `${profile.firstName} ${profile.lastName}`;
          }
          else if (profile.firstName !== 'Unknown') {
            displayName = profile.firstName;
          }
          else if (profile.lastName !== 'User') {
            displayName = profile.lastName;
          }
          else {
            displayName = `User-${profile.keycloakId.split('-')[0]}`;
          }
          return displayName;
        }
      }

      // Then check global cache
      const cachedProfile = this.findProfileByAcronym(acronym);
      if (cachedProfile) {
        let displayName: string;
        if (cachedProfile.firstName !== 'Unknown' && cachedProfile.lastName !== 'User') {
          displayName = `${cachedProfile.firstName} ${cachedProfile.lastName}`;
        }
        else if (cachedProfile.firstName !== 'Unknown') {
          displayName = cachedProfile.firstName;
        }
        else if (cachedProfile.lastName !== 'User') {
          displayName = cachedProfile.lastName;
        }
        else {
          displayName = `User-${cachedProfile.keycloakId.split('-')[0]}`;
        }
        return displayName;
      }

      // If no match found, return the original acronym
      return acronym;
    });

    return processedText;
  }

  triggerMarkdownReRender(): void {
    this.markdownReRenderSubject.next();
  }

  forceMarkdownReRender(): void {
    this.triggerMarkdownReRender();
  }

  private loadGlobalCacheFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.GLOBAL_CACHE_KEY);
      if (stored) {
        const data: Array<[string, SimpleProfile]> = JSON.parse(stored);
        this.globalProfiles = new Map(data);
      }
    }
    catch (error) {
      this.globalProfiles.clear();
    }
  }

  private saveGlobalCacheToStorage(): void {
    try {
      const data = Array.from(this.globalProfiles.entries());
      localStorage.setItem(this.GLOBAL_CACHE_KEY, JSON.stringify(data));
    }
    catch (error) {
    }
  }

  private loadHiddenProfilesFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.HIDDEN_CACHE_KEY);
      if (stored) {
        const data: Array<[number, string[]]> = JSON.parse(stored);
        this.hiddenProfiles = new Map(data.map(([chatId, ids]) => [chatId, new Set(ids)]));
      }
    }
    catch (error) {
      this.hiddenProfiles.clear();
    }
  }

  private saveHiddenProfilesToStorage(): void {
    try {
      const data = Array.from(this.hiddenProfiles.entries())
        .map(([chatId, hidden]) => [chatId, Array.from(hidden)]);
      localStorage.setItem(this.HIDDEN_CACHE_KEY, JSON.stringify(data));
    }
    catch (error) {
    }
  }

  private addToGlobalCache(keycloakId: string, firstName: string, lastName: string): void {
    if (keycloakId && (firstName || lastName)) {
      const name = firstName && lastName ? `${firstName} ${lastName}` : (firstName || lastName || `User-${keycloakId.split('-')[0]}`);
      const acronym = keycloakId.slice(0, 6); // Generate 6-character acronym

      this.globalProfiles.set(keycloakId, {
        id: keycloakId,
        name,
        acronym,
        cached: Date.now()
      });

      this.saveGlobalCacheToStorage();
    }
  }

  private getNameFromGlobalCache(keycloakId: string): string | null {
    const cached = this.globalProfiles.get(keycloakId);
    if (cached) {
      const age = Date.now() - cached.cached;
      if (age < this.GLOBAL_CACHE_DURATION) {
        return cached.name;
      }
      else {
        this.globalProfiles.delete(keycloakId);
        this.saveGlobalCacheToStorage();
      }
    }
    return null;
  }

  private getAcronymFromGlobalCache(keycloakId: string): string | null {
    const cached = this.globalProfiles.get(keycloakId);
    if (cached) {
      const age = Date.now() - cached.cached;
      if (age < this.GLOBAL_CACHE_DURATION) {
        return cached.acronym;
      }
      else {
        this.globalProfiles.delete(keycloakId);
        this.saveGlobalCacheToStorage();
      }
    }
    return null;
  }

  private populateGlobalCacheFromProfiles(profiles: ChatbotProfile[]): void {
    profiles.forEach(profile => {
      if (profile.keycloakId && (profile.firstName || profile.lastName)) {
        this.addToGlobalCache(profile.keycloakId, profile.firstName, profile.lastName);
      }
    });
  }

  private findProfileByAcronym(acronym: string): ChatbotProfile | null {
    // First check current profiles
    const currentProfiles = this.getCurrentProfiles();
    if (currentProfiles && currentProfiles.length > 0) {
      const profile = currentProfiles.find(p => p.acronym === acronym);
      if (profile) {
        return profile;
      }
    }

    // Then check global cache
    for (const [keycloakId, cached] of this.globalProfiles.entries()) {
      const age = Date.now() - cached.cached;
      if (age < this.GLOBAL_CACHE_DURATION && cached.acronym === acronym) {
        const nameParts = cached.name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        return new ChatbotProfile(
          keycloakId,
          'ACTIVE',
          cached.acronym,
          firstName,
          lastName,
          TestVariation.ID37_14.motiveCount
        );
      }
    }

    return null;
  }

  getProfileFromGlobalCache(keycloakId: string): ChatbotProfile | null {
    const cached = this.globalProfiles.get(keycloakId);
    if (cached) {
      const age = Date.now() - cached.cached;
      if (age < this.GLOBAL_CACHE_DURATION) {
        const nameParts = cached.name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        return new ChatbotProfile(
          keycloakId,
          'ACTIVE',
          keycloakId.slice(0, 6), // Use first 6 characters as acronym
          firstName,
          lastName,
          TestVariation.ID37_14.motiveCount
        );
      }
      else {
        this.globalProfiles.delete(keycloakId);
        this.saveGlobalCacheToStorage();
      }
    }
    return null;
  }

  getProfileByAcronym(acronym: string): ChatbotProfile | null {
    return this.findProfileByAcronym(acronym);
  }

  getAcronymFromKeycloakId(keycloakId: string): string | null {
    // First check current profiles
    const currentProfiles = this.getCurrentProfiles();
    if (currentProfiles && currentProfiles.length > 0) {
      const profile = currentProfiles.find(p => p.keycloakId === keycloakId);
      if (profile) {
        return profile.acronym;
      }
    }

    // Then check global cache
    return this.getAcronymFromGlobalCache(keycloakId);
  }
}
